import type { LocaleCatalog } from "../types";

const n = (event: { payload?: Record<string, string | number | boolean> }, key: string, fallback: string | number) =>
  event.payload?.[key] ?? fallback;

export const en = {
  ui: {
    brandTagline: "INTERSYSTEM CARTOGRAPHY",
    introSub: "KX / MIDGARD RELAY",
    introTitle: "THE SPACE BETWEEN SYSTEMS",
    systemsNominal: "CONSTELLATION NOMINAL",
    command: "COMMAND",
    commandPaletteLabel: "K·NEXUS command palette",
    commandPlaceholder: "Search systems, worlds, orbital identities…",
    commandEmpty: "No signal at these coordinates.",
    navHeading: "NAVIGATION",
    entitiesHeading: "ENTITIES & ARTIFACTS",
    orbitalHeading: "ORBITAL IDENTITIES",
    returnToCosmos: "Return to the chart",
    returnToCosmosDetail: "Re-enter the full intersystem chart",
    navigate: "NAVIGATE",
    focus: "FOCUS",
    close: "CLOSE",
    allSystems: "ALL SYSTEMS",
    map: "MAP",
    frame: "FRAME",
    local: "LOCAL",
    cosmos: "COSMOS",
    find: "FIND",
    sound: "SOUND",
    access: "ACCESS",
    focusLabel: "FOCUS",
    edge: "EDGE",
    data: "DATA",
    hybridMock: "HYBRID MOCK",
    live: "LIVE",
    fallback: "FALLBACK",
    activity: "ACTIVITY",
    liveEventFabric: "LIVE SIGNAL FABRIC",
    click: "CLICK",
    escape: "ESC",
    hideUi: "HIDE UI",
    closeInspector: "Close inspector",
    expandInspector: "Expand inspector details",
    collapseInspector: "Collapse inspector details",
    stateVector: "STATE VECTOR",
    lastSignal: "LAST SIGNAL",
    revision: "REVISION",
    objects: "OBJECTS",
    class: "CLASS",
    connectedEntities: "CONNECTED ENTITIES",
    systemComponents: "SYSTEM COMPONENTS",
    recentSignals: "RECENT SIGNALS",
    openSystem: "CROSS TO SYSTEM",
    unknown: "UNKNOWN",
    orbitalIdentity: "ORBITAL IDENTITY",
    parentSystem: "PARENT SYSTEM",
    orbitalClass: "ORBITAL CLASS",
    languageSelector: "Language",
    switchLanguage: (value) => `Switch language to ${value}`,
    semanticMap: "Constellation navigator",
    semanticMapHint: "Keyboard-accessible chart of the living constellation",
    accessibleObservatory: "Accessible observatory",
    accessibleObservatoryOn: "Accessible observatory enabled",
    accessibleObservatoryOff: "Accessible observatory disabled",
    navProduct: "Product",
    navHowItWorks: "How it works",
    navSolutions: "Solutions",
    navResources: "Resources",
    navAboutUs: "About us",
    getStarted: "Get Started",
    heroHeadlineLine1: "Between worlds.",
    heroHeadlineLine2Prefix: "Enter the",
    heroHeadlineLine2Accent: "Nexus.",
    heroDescription:
      "A live kaim.dev relay — manifests, edges and state mapped as one navigable cosmos.",
    startMapping: "Enter Nexus",
    watchVision: "Focus the Core",
    demoTour: "Survey Route",
    exitDemoTour: "End Survey",
    heroEyebrow: "MIDGARD RELAY / LIVE",
    nowLabel: "NOW",
    overviewLabel: "Atlas",
  },
  nodes: {
    kaimDev: {
      label: "KAIM.DEV",
      eyebrow: "IDENTITY / PORTFOLIO",
      description: "Primary public identity layer and portfolio frontier of the ecosystem.",
    },
    goals: {
      label: "K·100",
      eyebrow: "GOALS / FUTURES",
      description: "A living atlas of one hundred goals, forecasts and prediction-market state.",
    },
    build: {
      label: "K·BUILD",
      eyebrow: "HOUSE / MICHAŁOWICE",
      description: "End-to-end construction knowledge, state and process system for the Michałowice house.",
    },
    photos: {
      label: "PHOTOS",
      eyebrow: "ARCHIVE / MEMORY",
      description: "photos.kaim.dev — the photographic archive platform hosting private, named instances.",
    },
    breakglass: {
      label: "BREAKGLASS",
      eyebrow: "SATIRE / AI",
      description: "A satirical emergency interface about excessive AI exposure.",
    },
    home: {
      label: "HOME",
      eyebrow: "AUTOMATION / PHYSICAL",
      description: "Future Home Assistant bridge and physical-state telemetry for the house.",
    },
    okolica: {
      label: "OKOLICA DZIAŁKI",
      eyebrow: "LOCATION INTELLIGENCE",
      description: "Parcel-level location intelligence: planning, mobility, terrain and environmental context.",
    },
    michalowice: {
      label: "MICHAŁOWICE",
      eyebrow: "PROPERTY / 841 M²",
      description: "Primary physical anchor: the selected plot and future house location.",
    },
    houseGoal: {
      label: "HOUSE GOAL",
      eyebrow: "GOAL / RESIDENTIAL",
      description: "Long-horizon objective describing the transition from plot acquisition to a finished home.",
    },
    house: {
      label: "HOUSE",
      eyebrow: "PHYSICAL / RESIDENTIAL",
      description: "The physical house being designed and built on the Michałowice plot.",
    },
    marketEngine: {
      label: "MARKET ENGINE",
      eyebrow: "LMSR / INTEGRITY",
      description: "Sequenced LMSR state, ledger and portfolio projection behind K·100 Futures.",
    },
    portfolioFrontier: {
      label: "PORTFOLIO FRONTIER",
      eyebrow: "WORKER / DEPLOYMENT",
      description: "Cloudflare Worker deployment surface serving the public portfolio.",
    },
    photoArchive: {
      label: "COLLECTION 01",
      eyebrow: "20,769 OBJECTS",
      description: "Indexed source collection behind the photographic archive surface.",
    },
    aferyLekarskie: {
      label: "MEDICAL SCANDALS",
      eyebrow: "SATIRE / GENERATOR",
      description: "A lightweight static generator of fictional medical scandals with no database or user accounts.",
    },
  },
  orbitals: {
    goalsDo: {
      label: "INTEGRITY COORDINATOR",
      eyebrow: "DURABLE OBJECT / SEQUENCER",
      description: "State sequencer ensuring trade ordering, integrity checks and deterministic market projections.",
    },
    goalsD1: {
      label: "MARKET D1",
      eyebrow: "STATE / POSITIONS / LEDGER",
      description: "Relational storage for accounts, positions, ledger state and aggregate market projections.",
    },
    goalsFuturesUi: {
      label: "FUTURES UI",
      eyebrow: "MARKET SURFACE / INTERACTION",
      description: "User-facing market surface for positions, prices, portfolios and guidance.",
    },
    goalsLedger: {
      label: "LEDGER STREAM",
      eyebrow: "APPEND-ONLY / TRADES",
      description: "Append-only trade history and event stream feeding market state reconstruction.",
    },
    kaimWorker: {
      label: "PORTFOLIO FRONTIER",
      eyebrow: "CLOUDFLARE WORKER / EDGE",
      description: "Primary edge runtime serving the portfolio and orchestrating public-facing routes.",
    },
    kaimStudioApi: {
      label: "STUDIO API",
      eyebrow: "CONTENT / STUDIO SURFACE",
      description: "Internal API layer supporting content operations and future studio capabilities.",
    },
    kaimAccess: {
      label: "ZERO TRUST ACCESS",
      eyebrow: "ACCESS / PROTECTION",
      description: "Access layer protecting private interfaces and gating internal surfaces.",
    },
    kaimDesignSystem: {
      label: "DESIGN LAYER",
      eyebrow: "TYPOGRAPHY / MOTION / TOKENS",
      description: "Shared visual language spanning layout, motion and typography choices across the ecosystem.",
    },
    kaimNexusConnector: {
      label: "NEXUS CONNECTOR",
      eyebrow: "MCP / API / GRAPH",
      description:
        "The machine-facing relay between kaim.dev worlds: manifests become coordinates and service edges become routes.",
    },
    photosArchiveIndex: {
      label: "ARCHIVE INDEX",
      eyebrow: "MATERIALIZED / SEARCH",
      description: "Materialized photo index and summary generated during normal archive indexing.",
    },
    photosObjectStore: {
      label: "OBJECT STORE",
      eyebrow: "MEDIA / STORAGE",
      description:
        "Storage layer behind Photos; Nexus consumes only aggregate metadata and never lists objects on demand.",
    },
    photosRozaInstance: {
      label: "RÓŻA INSTANCE",
      eyebrow: "roza.photos.kaim.dev / PRIVATE",
      description:
        "A concrete private Photos instance represented only by redacted health, revision and index metadata.",
    },
    homeAssistant: {
      label: "HOME ASSISTANT",
      eyebrow: "AUTOMATION / CONTROL",
      description: "Future local automation control plane for devices, scenes and house state.",
    },
    homePhysicalHost: {
      label: "PHYSICAL HOST",
      eyebrow: "NETTOP / LOCAL",
      description: "Local host intended to run Home Assistant and bridge physical state into the ecosystem.",
    },
    homeRemoteAccess: {
      label: "REMOTE ACCESS",
      eyebrow: "smarthome.kaim.dev / EDGE",
      description:
        "Guarded remote entry point to the home control plane without exposing raw local telemetry to Nexus.",
    },
    okolicaCadastrePlanning: {
      label: "CADASTRE + PLANNING",
      eyebrow: "ULDK / MPZP",
      description: "Parcel boundaries and planning context materialized for the selected plot.",
    },
    okolicaTerrainEnvironment: {
      label: "TERRAIN + ENVIRONMENT",
      eyebrow: "SLOPE / RISK / CONTEXT",
      description: "Precomputed terrain and environmental summary used by parcel intelligence.",
    },
    okolicaMobility: {
      label: "MOBILITY",
      eyebrow: "ROADS / ACCESS",
      description: "Precomputed access and mobility summary rather than live routing calls from Nexus.",
    },
    okolicaEvidenceExport: {
      label: "EVIDENCE EXPORT",
      eyebrow: "GEOJSON / SNAPSHOT",
      description: "Versioned evidence artifact that can be consumed by K·BUILD without rerunning location analysis.",
    },
    breakglassEdgeRuntime: {
      label: "EDGE RUNTIME",
      eyebrow: "CLOUDFLARE / DELIVERY",
      description: "Public edge runtime serving Breakglass scenarios and static system metadata.",
    },
    breakglassScenarioEngine: {
      label: "SCENARIO ENGINE",
      eyebrow: "REGISTRY / MODES",
      description: "Scenario registry and deterministic mode configuration exposed to Nexus only as summary metadata.",
    },
    buildBrief: {
      label: "ARCHITECTURAL BRIEF",
      eyebrow: "PROGRAM / REQUIREMENTS",
      description: "Structured architectural brief describing spatial intent, program and constraints.",
    },
    buildDocuments: {
      label: "DOCUMENT ATLAS",
      eyebrow: "FILES / DECISIONS / EVIDENCE",
      description: "Knowledge layer linking documents, decisions, milestones and process evidence.",
    },
    buildPlotIntel: {
      label: "PLOT INTELLIGENCE",
      eyebrow: "TERRAIN / PLANNING / CONTEXT",
      description: "Analytical parcel context flowing in from Okolica Działki and related planning inputs.",
    },
    buildTimeline: {
      label: "MILESTONE TIMELINE",
      eyebrow: "PROCESS / STAGES",
      description: "Milestone system tracking project phases, approvals and construction readiness.",
    },
  },
  events: {
    "build.brief.updated": (e) => ({
      title: `Architectural brief v${n(e, "version", 3)} accepted`,
      detail: "Program moved into structured pre-design state.",
    }),
    "goal.progress.updated": (e) => ({
      title: `Goal #${n(e, "goal", 34)} → ${n(e, "progress", 48)}%`,
      detail: "Progress projection updated.",
    }),
    "portfolio.deployed": (e) => ({
      title: "portfolio-frontier deployed",
      detail: `Production edge revision ${n(e, "revision", "unknown")}.`,
    }),
    "photos.indexed": (e) => ({
      title: "Collection manifest rebuilt",
      detail: `${Number(n(e, "count", 0)).toLocaleString("en-US")} source objects indexed.`,
    }),
    "home.simulation": () => ({
      title: "Home node waiting for physical host",
      detail: "Telemetry remains simulated in the current prototype.",
    }),
    "market.integrity": () => ({
      title: "Market integrity coordinator healthy",
      detail: "Sequenced state projection available.",
    }),
    "build.state.sampled": () => ({
      title: "Build state synchronized",
      detail: "K·BUILD published a fresh materialized construction snapshot.",
    }),
    "goals.market.sampled": () => ({
      title: "K·100 market snapshot reconciled",
      detail: "Mock market projection matched the current ledger head.",
    }),
    "portfolio.edge.sampled": () => ({
      title: "Edge deployment heartbeat",
      detail: "portfolio-frontier responded within nominal envelope.",
    }),
    "photos.manifest.sampled": () => ({
      title: "Archive index heartbeat",
      detail: "Collection manifest remains available.",
    }),
    "home.awaiting-host": () => ({
      title: "Physical node not provisioned",
      detail: "Expected in the current mock-only phase.",
    }),
    "location.context.sampled": () => ({
      title: "Parcel context refreshed",
      detail: "Location intelligence mock projection refreshed.",
    }),
  },
} satisfies LocaleCatalog;
